/* eslint-disable no-redeclare */
/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { TFunction } from 'i18next';
import { cloneDeep } from 'lodash';
import type { InitialMapProps } from '@opengeoweb/core';
import { snackbarTypes } from '@opengeoweb/snackbar';
import type { MapPreset } from '@opengeoweb/store';
import { PresetFilterType } from './types';
import type {
  WorkspacePreset,
  WorkspacePresetListItem,
  WorkspaceState,
  WorkspaceTemplatePreset,
  WorkspaceViewsFromBE,
} from '../workspace/types';
import { WorkspacePresetAction } from '../workspace/types';

export const getSnackbarMessage = (
  action: WorkspacePresetAction,
  title: string,
  is_shared?: boolean,
): snackbarTypes.TranslatableMessage => {
  // Trim and add quotes
  const trimmedTitle = title.trim();
  switch (action) {
    case WorkspacePresetAction.DELETE:
      return {
        type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
        key: 'workspace-list-success-delete',
        params: { TITLE: trimmedTitle },
      };
    case WorkspacePresetAction.DELETE_SYSTEM_PRESET:
      return {
        type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
        key: 'workspace-list-success-delete-system',
        params: {
          TITLE: trimmedTitle,
        },
      };
    case WorkspacePresetAction.DUPLICATE:
      return {
        type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
        key: 'workspace-list-success-duplicate',
        params: { TITLE: trimmedTitle },
      };
    case WorkspacePresetAction.EDIT:
      return {
        type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
        key: 'workspace-list-success-edit',
        params: { TITLE: trimmedTitle },
      };
    case WorkspacePresetAction.RESET:
      return {
        type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
        key: 'workspace-list-success-reset',
        params: {
          TITLE: trimmedTitle,
        },
      };
    case WorkspacePresetAction.SHARE:
      return {
        type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
        key: is_shared
          ? 'workspace-list-success-unshare'
          : 'workspace-list-success-share',
        params: {
          TITLE: trimmedTitle,
        },
      };
    default:
      return {
        type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
        key: 'workspace-list-success-save',
        params: { TITLE: trimmedTitle },
      };
  }
};

export function getEmptyWorkspaceListItem(): Omit<
  WorkspacePresetListItem,
  'title'
>;
export function getEmptyWorkspaceListItem(
  t: TFunction,
): WorkspacePresetListItem;
export function getEmptyWorkspaceListItem(
  t?: TFunction,
): Omit<WorkspacePresetListItem, 'title'> | WorkspacePresetListItem;
export function getEmptyWorkspaceListItem(
  t?: TFunction,
): Omit<WorkspacePresetListItem, 'title'> | WorkspacePresetListItem {
  return {
    id: undefined!,
    title: t ? t('workspace-new') : undefined,
    labels: undefined,
    scope: 'system',
    abstract: '',
    viewType: 'singleWindow',
  };
}

export function getEmptyMapWorkspace(): Omit<WorkspacePreset, 'title'>;
export function getEmptyMapWorkspace(t: TFunction): WorkspacePreset;
export function getEmptyMapWorkspace(
  t?: TFunction,
): WorkspacePreset | Omit<WorkspacePreset, 'title'> {
  return {
    ...getEmptyWorkspaceListItem(t),

    views: {
      allIds: ['1_screen'],
      byId: {
        '1_screen': {
          title: t ? t('workspace-mappreset-new') : undefined,
          componentType: 'Map',
          initialProps: {
            mapPreset: {
              shouldShowLayerManager: true,
              dockedLayerManagerSize: 'sizeSmall',
            },
            syncGroupsIds: [],
          },
        },
      },
    },
    mosaicNode: '1_screen',
  };
}

export const getEmptyWorkspacePreset = (): Omit<WorkspacePreset, 'title'> => ({
  scope: 'system',
  abstract: '',
  views: {
    allIds: ['1_screen'],
    byId: {
      '1_screen': {
        componentType: 'Map',
        initialProps: {
          mapPreset: {
            shouldShowLayerManager: true,
            dockedLayerManagerSize: 'sizeSmall',
          },
          syncGroupsIds: [],
        },
      },
    },
  },
  mosaicNode: '1_screen',
  viewType: 'singleWindow',
  username: 'global',
});

export const createCustomEmptyMapWorkspace = (
  emptyWorkspaceTitle: string,
  emptyMapWorkspaceTitle: string,
  customMapPreset?: MapPreset,
): WorkspacePreset => {
  const customEmptyMapWorkspace = cloneDeep(getEmptyWorkspacePreset());
  if (customMapPreset) {
    (
      customEmptyMapWorkspace.views.byId!['1_screen']
        .initialProps! as InitialMapProps
    ).mapPreset = customMapPreset;
  }

  customEmptyMapWorkspace.views.byId!['1_screen'].title =
    emptyMapWorkspaceTitle;
  const emptyWorkspacePreset: WorkspacePreset = {
    ...customEmptyMapWorkspace,
    title: emptyWorkspaceTitle,
  };
  return emptyWorkspacePreset;
};

export const getTemplatePresetTitle = (
  preset: Pick<WorkspaceTemplatePreset, 'title' | 'titleKey'>,
  t?: TFunction,
): string =>
  preset.titleKey && t
    ? t(preset.titleKey, { defaultValue: preset.title })
    : preset.title;

export const LOCAL_SOUNDINGS_ID = 'newSoundingsWorkspace';

export const getNewSoundingsWorkspaceListItem = (
  t?: TFunction,
): WorkspacePresetListItem => {
  return {
    id: LOCAL_SOUNDINGS_ID,
    title: t ? t('workspace-new-soundings') : 'New soundings workspace',
    labels: undefined,
    scope: 'system',
    abstract: '',
    viewType: 'multiWindow',
  };
};

export function getNewSoundingsWorkspace(t?: TFunction): WorkspacePreset {
  return {
    ...getNewSoundingsWorkspaceListItem(t),
    views: {
      allIds: ['1_screen', '2_screen'],
      byId: {
        '1_screen': {
          mosaicNodeId: '1_screen',
          title: 'New soundings map',
          componentType: 'Map',
          initialProps: {
            mapPreset: {
              shouldShowLegend: false,
              shouldShowLayerManager: false,
            },
          },
        },
        '2_screen': {
          mosaicNodeId: '2_screen',
          title: 'New soundings view',
          componentType: 'Soundings',
          initialProps: {},
        },
      },
    },
    linking: { '2_screen': ['1_screen'] },
    syncGroups: [
      {
        id: 'Features_2_screen',
        sourcePanelId: '2_screen',
        type: 'SYNCGROUPS_TYPE_SETFEATURES',
      },
    ],
    mosaicNode: { direction: 'row', first: '1_screen', second: '2_screen' },
  };
}

export const getBEViewsFromState = (
  workspace: WorkspaceState,
): WorkspaceViewsFromBE[] => {
  const result: WorkspaceViewsFromBE[] = [];
  (workspace?.views?.allIds ?? []).forEach((id: string) => {
    const view = workspace.views.byId[id] ?? {};
    result.push({
      mosaicNodeId: id,
      title:
        typeof view.title === 'string' && view.title.trim() ? view.title : id,
      componentType: view.componentType,
      initialProps: view.initialProps,
    });
  });
  return result;
};

export const togglePresetFilter = <
  T extends { id: string; isSelected?: boolean },
>(
  filters: T[],
  id: PresetFilterType,
  isSelected: boolean,
  isAdmin = false,
  isFavouritesEnabled = true,
): T[] => {
  if (!filters.some((filter) => filter.id === id)) {
    return filters;
  }
  const availableFilters = filters.filter((filter) => {
    if (filter.id === PresetFilterType.favourites) {
      return isFavouritesEnabled;
    }
    return (
      !isAdmin ||
      filter.id === PresetFilterType.system ||
      filter.id === PresetFilterType.shared ||
      filter.id === PresetFilterType.templates
    );
  });
  const allSelected = availableFilters.every((filter) => filter.isSelected);
  const allUnselected = availableFilters.every((filter) =>
    filter.id === id ? !isSelected : !filter.isSelected,
  );
  return filters.map((filter) => {
    if (allSelected) {
      return { ...filter, isSelected: filter.id === id && isSelected };
    }
    if (
      isAdmin &&
      id !== PresetFilterType.favourites &&
      filter.id !== PresetFilterType.favourites
    ) {
      return { ...filter, isSelected: filter.id === id };
    }
    return {
      ...filter,
      isSelected:
        allUnselected || (filter.id === id ? isSelected : filter.isSelected),
    };
  });
};
